import React, { useState, useEffect } from 'react';
import { X, Save, Check } from 'lucide-react';
import axios from 'axios';

const EditViewerPermissionsModal = ({ isOpen, onClose, viewer, onSuccess }) => {
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen && viewer) {
      fetchAvailablePermissions();
      // Initialize with viewer's current permissions
      setSelectedPermissions(viewer.permissions || {});
    }
  }, [isOpen, viewer]);

  const fetchAvailablePermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/viewer/permissions`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setAvailablePermissions(response.data.permissions);
      }
    } catch (err) {
      console.error('Error fetching permissions', err);
    }
  };

  const togglePermission = (key) => {
    setSelectedPermissions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleAll = () => {
    const allSelected = Object.values(selectedPermissions).every(v => v);
    const newPerms = {};
    availablePermissions.forEach(p => {
      newPerms[p.key] = !allSelected;
    });
    setSelectedPermissions(newPerms);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/viewer/update/${viewer.id}`,
        {
          permissions: selectedPermissions
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update permissions');
      console.error('Error updating viewer permissions', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedPermissions({});
    setError('');
    setSuccess(false);
    onClose();
  };

  if (!isOpen || !viewer) return null;

  const allSelected = Object.values(selectedPermissions).every(v => v);
  const selectedCount = Object.values(selectedPermissions).filter(v => v).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Edit Viewer Permissions</h2>
              <p className="text-blue-100 text-sm mt-1">{viewer.viewer_name}</p>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Select All Toggle */}
          <div className="mb-6 flex items-center justify-between bg-gray-50 p-4 rounded-lg">
            <span className="font-semibold text-gray-700">
              {selectedCount} of {availablePermissions.length} permissions selected
            </span>
            <button
              type="button"
              onClick={toggleAll}
              className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Permissions Grid */}
          <div className="space-y-3 mb-6">
            {availablePermissions.map((permission) => {
              const isSelected = selectedPermissions[permission.key];
              return (
                <div
                  key={permission.key}
                  onClick={() => togglePermission(permission.key)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300'
                    }`}>
                      {isSelected && <Check size={14} className="text-white" />}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{permission.label}</h4>
                      <p className="text-sm text-gray-600 mt-1">{permission.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 flex items-center gap-2">
              <Check size={20} />
              Permissions updated successfully!
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || success}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[44px]"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={20} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditViewerPermissionsModal;
